import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import * as moment from 'moment';
import { LocalStorageService } from 'ngx-localstorage';
import { Observable, of } from 'rxjs';
import { catchError, mapTo, switchMap, tap } from 'rxjs/operators';
import { FormService } from '../shared/form.service';
import { OfferService } from '../shared/offer.service';
import { ProposalService } from '../shared/proposal.service';
import { SharedService } from '../shared/shared.service';

@Injectable({
  providedIn: 'root'
})
export class CheckPolicyDataGuard implements CanActivate {
  constructor(
    private offerService: OfferService,
    public proposalService: ProposalService,
    private sharedService: SharedService,
    private storage: LocalStorageService,
    private formService: FormService) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!this.proposalService.calculatedPremiums$) {
      const data = this.storage.get('data');
      this.formService.getMainForm().patchValue(data);
      this.formService.getMainForm().get('insuranceStart').setValue(moment(data.insuranceStart));
      const formData = this.formService.getMainForm().getRawValue();
      this.sharedService.getProductProposals(this.getProductProposalsPayload(formData), this.formService.currentForm);
      this.proposalService.calculatePremiums(this.getCalculatedPremiumsPayload(formData));
      return true;
    } else {
      if(this.formService.currentForm.value.nIDAgent){
        this.sharedService.loadingSpinner = true;
        return this.sharedService.getAgentId(this.formService.currentForm.value.nIDAgent).pipe(
          switchMap(res => {
            this.formService.currentForm.controls.nIdAgentNumber.patchValue(res.toString())
            return this.offerService.issuePolicy();
          }),
          tap(data => {
            const response = data as any;
            const form = this.formService.getMainForm();
            const formData = form.getRawValue();
            form.get('nsumm').setValue(response.nsumm);
            form.get('nsummCurr2').setValue(this.getFirstInstallmentSecondCurrency(response.nsumm, formData));
            form.get('sPlcNomer').setValue(response.sPlcNomer);
          }),
          mapTo(true),
          catchError(error => of(false))
        );
      }
      else {
        return this.offerService.issuePolicy()
        .pipe(
          tap(data => {
            const response = data as any;
            const form = this.formService.getMainForm();
            const formData = form.getRawValue();
            form.get('nsumm').setValue(response.nsumm);
            form.get('nsummCurr2').setValue(this.getFirstInstallmentSecondCurrency(response.nsumm, formData));
            form.get('sPlcNomer').setValue(response.sPlcNomer);
          }),
          mapTo(true),
          catchError(error => of(false))
        )
      }
   
    }
  }

  private getProductProposalsPayload(formData) {
    return {
      "area": String(formData.area),
      "city": formData.cityName,
      "claimsExists": formData.claimsExists,
      "currency": formData.currency,
      "immovableinsuranceSum": formData.immovableinsuranceSum,
      "immovableinsuranceSumRecommended": formData.immovableinsuranceSumRecommended,
      "immovableinsuranceSumRecommendedCurr2": formData.immovableinsuranceSumRecommendedCurr2,
      "movableinsuranceSum": formData.movableinsuranceSum,
      "movableinsuranceSumRecommended": formData.movableinsuranceSumRecommended,
      "movableinsuranceSumRecommendedCurr2": formData.movableinsuranceSumRecommendedCurr2,
      "postCode": formData.postCode,
      "propertyCategory": formData.propertyCategory,
      "propertySubcategory": formData.propertySubcategory,
      "purpose": formData.purpose,
      "street": formData.street,
      "yearOfConstruction": formData.yearOfConstruction
    }
  }

  private getCalculatedPremiumsPayload(formData) {
    return {
      "area": formData.area,
      "bottomComfortFour": formData.bottomComfortFour,
      "bottomComfortOne": formData.bottomComfortOne,
      "bottomComfortTwo": formData.bottomComfortTwo,
      "bottomExtraFour": formData.bottomExtraFour,
      "bottomExtraOne": formData.bottomExtraOne,
      "bottomExtraTwo": formData.bottomExtraTwo,
      "bottomMaxFour": formData.bottomMaxFour,
      "bottomMaxOne": formData.bottomMaxOne,
      "bottomMaxTwo": formData.bottomMaxTwo,
      "bottomComfortOneCurr2" : formData.bottomComfortOneCurr2,
      "bottomExtraOneCurr2" : formData.bottomExtraOneCurr2,
      "bottomMaxOneCurr2" : formData.bottomMaxOneCurr2,
      "bottomComfortTwoCurr2" : formData.bottomComfortTwoCurr2,
      "bottomExtraTwoCurr2" : formData.bottomExtraTwoCurr2,
      "bottomMaxTwoCurr2" : formData.bottomMaxTwoCurr2,
      "bottomComfortFourCurr2" : formData.bottomComfortFourCurr2,
      "bottomExtraFourCurr2" : formData.bottomExtraFourCurr2,
      "bottomMaxFourCurr2" : formData.bottomMaxFourCurr2,
      "chosenPackageId": formData.chosenPackageId,
      "claimsExists": formData.claimsExists,
      "commissionDiscount": formData.commissionDiscount,
      "contractProcessState": formData.contractProcessState,
      "currency": formData.currency,
      "glass": formData.glass,
      "goLimit": formData.goLimit,
      "immovableinsuranceSumRecommended": formData.immovableinsuranceSumRecommended,
      "immovableinsuranceSumRecommendedCurr2": formData.immovableinsuranceSumRecommendedCurr2,
      "insSumOutsideObjects": formData.insSumOutsideObjects,
      "insSumSpecificObjects": formData.insSumSpecificObjects,
      "isDeductibleIncluded": formData.isDeductibleIncluded,
      "isEarthQuakeIncluded": formData.isEarthQuakeIncluded,
      "isGlassIncluded": formData.isGlassIncluded,
      "isGoMaxIncluded": formData.isGoMaxIncluded,
      "isRenewed": formData.isRenewed,
      "movableinsuranceSumRecommended": formData.movableinsuranceSumRecommended,
      "movableinsuranceSumRecommendedCurr2": formData.movableinsuranceSumRecommendedCurr2,
      "n_1NSP_Premium": formData.n_1NSP_Premium,
      "n_2NSP_Premium": formData.n_2NSP_Premium,
      "n_3NSP_Premium": formData.n_3NSP_Premium,
      "n_1NSP_PremiumCurr2" : formData.n_1NSP_PremiumCurr2,
      "n_2NSP_PremiumCurr2" : formData.n_2NSP_PremiumCurr2,
      "n_3NSP_PremiumCurr2" : formData.n_3NSP_PremiumCurr2,
      "nErr_Code": formData.nErr_Code,
      "postCode": formData.postCode,
      "propertyCategory": formData.propertyCategory,
      "propertySubcategory": formData.propertySubcategory,
      "purpose": formData.purpose,
      "selectedPayments": formData.selectedPayments,
      "selfParticipation": formData.selfParticipation,
      "status": formData.status,
      "street": formData.street,
      "topComfort": formData.topComfort,
      "topExtra": formData.topExtra,
      "topMax": formData.topMax,
      "topComfortCurr2" : formData.topComfortCurr2,
      "topExtraCurr2" : formData.topExtraCurr2,
      "topMaxCurr2" : formData.topMaxCurr2,
      "yearOfConstruction": formData.yearOfConstruction
    }
  }

  private getFirstInstallmentSecondCurrency(installment: number, formData){
    if (installment === formData.bottomComfortOne){
      return formData.bottomComfortOneCurr2;
    } else if (installment === formData.bottomExtraOne){
      return formData.bottomExtraOneCurr2;
    } else if (installment === formData.bottomMaxOne){
      return formData.bottomMaxOneCurr2;
    } else if (installment === formData.bottomComfortTwo){
      return formData.bottomComfortTwoCurr2;
    } else if (installment === formData.bottomExtraTwo){
      return formData.bottomExtraTwoCurr2;
    } else if (installment === formData.bottomMaxTwo){
      return formData.bottomMaxTwoCurr2;
    } else if (installment === formData.bottomComfortFour){
      return formData.bottomComfortFourCurr2;
    } else if (installment === formData.bottomExtraFour){
      return formData.bottomExtraFourCurr2;
    } else if (installment === formData.bottomMaxFour){
      return formData.bottomMaxFourCurr2;
    }

    return null;
  }
}
